import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { prefsStore } from "@/app/preferences/prefsStore";
import { Density, DensityTokens, densityTokens } from "./densityTokens";

const STORAGE_KEY = "ui_density";

interface DensityContextValue {
  density: Density;
  setDensity: (d: Density) => void;
  tokens: DensityTokens;
}

const DensityContext = createContext<DensityContextValue | null>(null);

function readStored(): Density {
  try {
    const raw = prefsStore.getItem(STORAGE_KEY);
    return raw === "comfortable" ? "comfortable" : "compact";
  } catch {
    return "compact";
  }
}

export const DensityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [density, setDensityState] = useState<Density>(readStored);

  const setDensity = useCallback((d: Density) => {
    setDensityState(d);
    try {
      prefsStore.setItem(STORAGE_KEY, d);
    } catch {
      /* storage unavailable — keep in-memory value */
    }
  }, []);

  const value = useMemo<DensityContextValue>(
    () => ({ density, setDensity, tokens: densityTokens(density) }),
    [density, setDensity],
  );

  return <DensityContext.Provider value={value}>{children}</DensityContext.Provider>;
};

const FALLBACK: DensityContextValue = {
  density: "compact",
  setDensity: () => {},
  tokens: densityTokens("compact"),
};

export function useDensity(): DensityContextValue {
  return useContext(DensityContext) ?? FALLBACK;
}
